import React, { useState, useEffect } from "react";
import Loader from "../icons/Loader";
import { ErrorMessage } from "../Input/InputError";

// Check if device supports hover (not a touch-only device)
const canHover = () => {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(hover: hover)').matches;
};

const HoverBtn = ({ style, hoverStyle, children,className, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const [supportsHover, setSupportsHover] = useState(true);

  useEffect(() => {
    setSupportsHover(canHover());
  }, []);

  // Only apply hover styles if device supports hover
  const shouldShowHover = supportsHover && hovered;

  return (
    <div
      onClick={onClick}
      style={{ ...style, ...(shouldShowHover ? hoverStyle : {}) }}
      onMouseEnter={() => {
        if (supportsHover) {
          setHovered(true);
        }
      }}
      className={`${className || ''} ${shouldShowHover ? 'btn-hovered' : ''}`}
      onMouseLeave={() => {
        setHovered(false);
      }}
    >
      {children}
    </div>
  );
};

const Button = ({ error,submit=false, style, onClick, children, prompt, loading, messageStyle, className,btnClassName, fontSize, message, disabled, type = "primary", rounded = false, onPromptedChange }) => {
  const [prompted, setPrompted] = useState(false);

  const [internalLoading, setInternalLoading] = useState(false);

  useEffect(() => {
    onPromptedChange?.(prompted);
  }, [prompted, onPromptedChange]);

  const handleClick = (e) => {
    if (prompt && !prompted) {
      setPrompted(true);
    } else {
      onClick && onClick();
      setPrompted(false);
    }
  };

  const cancel = async (e) => {
    setInternalLoading(true);
    setPrompted(false);
    await new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 100);
    });
    setInternalLoading(false);
  };

  return (
    <div style={style} className={className}>
      {disabled ? (
        <div className={btnClassName} style={{ ...styles.button, ...styles.disabled, ...(btnClassName ? styles.disabledCustom : {}) }}>{children}</div>
      ) : (
        <>
          {loading || internalLoading ? (
            <div
              className="flex justify-center items-center"
            >
              <Loader />
            </div>
          ) : (
            <>
              {error && <ErrorMessage className='mb-kit-6 !text-kit-md'>{error}</ErrorMessage>}
              {prompted ? (
                <div style={styles.buttonContainers}>
                  <div style={{ ...styles.messageContainer, messageStyle }}>{message || "confirm?"}</div>
                  <div style={styles.promptActions}>
                    <HoverBtn
                      style={{ ...styles.button, ...(rounded ? styles.rounded : {}), ...styles.promptButton, ...styles.cancel.static, ...{ fontSize: fontSize } }}
                      hoverStyle={styles.cancel.hovered}
                      onClick={cancel}
                    >
                      no
                    </HoverBtn>
                    <HoverBtn
                      style={{ ...styles.button, ...(rounded ? styles.rounded : {}), ...styles.promptButton, ...styles[type]?.static, ...{ fontSize: fontSize } }}
                      hoverStyle={styles[type]?.hovered}
                      onClick={handleClick}
                    >
                      yes
                    </HoverBtn>
                  </div>
                </div>
              ) : (
                <HoverBtn
                className={btnClassName}
                  style={{ ...styles.button, ...(rounded ? styles.rounded : {}), ...styles[type]?.static, ...{ fontSize: fontSize } }}
                  hoverStyle={styles[type]?.hovered}
                  onClick={handleClick}
                >
                  {children}
                </HoverBtn>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  buttonContainers: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "var(--inyc-space-8)",
  },
  promptActions: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--inyc-space-8)",
    width: "100%",
  },
  messageContainer: {
    width: "100%",
    fontSize: "var(--inyc-text-md)",
    fontFamily: "Primary-Medium",
    fontWeight: 500,
    textAlign: "center",
    marginTop: "-10px",
  },
  promptButton: {
    width: "auto",
    minWidth: "8.5rem",
    padding: "var(--inyc-space-4) var(--inyc-space-20)",
  },
  error: {
    color: "red",
    fontSize: 20,
    marginBottom: "var(--inyc-space-20)",
    textAlign: "center",
  },
  button: {
    margin: "auto",
    padding: "var(--inyc-space-10) var(--inyc-space-24)",
    textAlign : 'center',
    borderRadius: "var(--inyc-button-border-radius)",
    fontSize: "var(--inyc-text-md)",
    fontFamily: "Primary-Medium",
    border: "1px solid var(--inyc-button-border-color)",
    transition: "0.3s",
    cursor: "pointer",
    width: "100%",
  },

  rounded: {
    borderRadius: "var(--inyc-radius-2xl)",
  },

  disabled: {
    backgroundColor: "var(--inyc-primary)",
    cursor: "default",
    color: "var(--inyc-primary-text-color-light)",
    boxShadow: "var(--inyc-shadow-sm)",
  },

  // custom-styled buttons keep their look when disabled, so dim them to read as disabled
  disabledCustom: {
    opacity: 0.5,
    cursor: "not-allowed",
  },

  primary: {
    static: {
      backgroundColor: "var(--inyc-primary-button)",
      boxShadow: "var(--inyc-button-shadow)",
      color: "var(--inyc-primary-text)",
    },
    hovered: {
      backgroundColor: "var(--inyc-secondary-button)",
      color: "var(--inyc-secondary-text-color)",
      boxShadow: "var(--inyc-button-shadow)",
    },
  },

  secondary : {
    static: {
      backgroundColor: "var(--inyc-secondary-button)",
      boxShadow: "var(--inyc-button-shadow)",
      color: "var(--inyc-secondary-text-color)",
    },
    hovered: {
      backgroundColor: "var(--inyc-primary-button)",
      color: "var(--inyc-primary-text-color)",
      boxShadow: "var(--inyc-button-shadow)",
    },
  },

  cancel: {
    static: {
      backgroundColor: "var(--inyc-primary)",
      color: "var(--inyc-primary-text-color)",
      border: "1px solid var(--inyc-outline-color)",
      boxShadow: "var(--inyc-button-shadow)",
    },
    hovered: {
      backgroundColor: "var(--inyc-secondary-button)",
      color: "var(--inyc-secondary-text-color)",
      border: "1px solid var(--inyc-outline-color)",
      boxShadow: "var(--inyc-button-shadow)",
    },
  },
};

export default Button;
